import { AxiosResponse, RawAxiosRequestHeaders } from 'axios'

import { CustomDOMEvents } from '@/enums/customDOMEvents'

import { BaseApiService } from './base'
import {
  TAnalyticsEvent,
  TRegisterUserPayload,
  TContactRegisterData,
  TSubscribePlanPayload,
  TSubscriptionPaymentIntentData,
  TPasswordCredentialsData,
  TLinkCredentialsData,
  TLTPData,
  TVerificationData,
  TVerificationPayload,
  TVerificationResendPayload,
  TReLoginPayload,
  TContactReLoginData,
  TSubscriptionPayPalPaymentIntentData,
  TContactStateData,
  TSubscriptionPaymentIntentV2Data,
  TCreatePayPalSubscriptionPayload,
  TCreatePayPalSubscriptionResponse,
  TSubscribeStripeV2Payload,
  TSubscribePayPalV2Payload,
  TSubscriptionPayPalPaymentIntentV2Data,
  TSendVerificationCodePayload,
  TVerificationV2Payload,
  TVerificationV2Data,
  TPurchaseContentPayload,
  TPurchaseContentData,
  TFastPurchaseContentPayload,
  TFastPurchaseContentData,
  TCreatePayPalPurchasePayload,
  TCreatePayPalPurchaseResponse,
  TApprovePayPalPurchasePayload,
  TApprovePayPalPurchaseIntentData,
} from './types'

const eventOnLogout = new Event(CustomDOMEvents.ON_401)

// {"error_code": exc.ERROR_CODE, "error_details": exc.get_details()},

declare module 'axios' {
  export interface AxiosRequestConfig {
    ignoreInterceptor401?: boolean
  }
}

class ApiServiceImpl extends BaseApiService {
  constructor() {
    super('backend')

    this.client.interceptors.response.use(
      (response) => {
        return response
      },
      (error) => {
        const status = error?.response?.status
        if (status === 401) {
          if (error.response.config?.ignoreInterceptor401) {
            console.log('ignoreInterceptor401')
          } else {
            document.dispatchEvent(eventOnLogout)
          }
        } else if (status === 400) {
          const errorData = error.response.data
          if (errorData && errorData.error_code === -1) {
            const url = errorData.error_details?.redirect_url
            if (url !== undefined) {
              window.location.href = url
            }
          }
        }
        return Promise.reject(error)
      }
    )
  }

  async registerUser(payload: TRegisterUserPayload): Promise<AxiosResponse<TContactRegisterData>> {
    return await this.client.post('/contact', payload)
  }

  async reLogin(payload: TReLoginPayload): Promise<AxiosResponse<TContactReLoginData>> {
    return await this.client.post('/contact/re-login', payload)
  }

  async verifyEmail(
    contactId: string,
    payload: TVerificationPayload
  ): Promise<AxiosResponse<TVerificationData>> {
    return await this.client.post(`/contact/${contactId}/verify-email-code`, payload)
  }

  async resendVerificationCode(
    contactId: string,
    payload: TVerificationResendPayload
  ): Promise<AxiosResponse> {
    return await this.client.post(`/contact/${contactId}/resend-verification-code`, payload)
  }

  async checkContactState(pageSetId: string): Promise<AxiosResponse<TContactStateData>> {
    return await this.client.get(`/contact/state`, { params: { page_set_id: pageSetId } })
  }

  async subscribePlan(
    payload: TSubscribePlanPayload
  ): Promise<AxiosResponse<TSubscriptionPaymentIntentData>> {
    return await this.client.post(`/contact/subscribe`, payload)
  }

  async subscribePayPalPlan(
    payload: TSubscribePlanPayload
  ): Promise<AxiosResponse<TSubscriptionPayPalPaymentIntentData>> {
    return await this.client.post(`/contact/subscribe/paypal`, payload)
  }

  async getPasswordCredentialsData(
    appId: string,
    ignoreInterceptor401?: boolean
  ): Promise<AxiosResponse<TPasswordCredentialsData>> {
    return await this.client.get(`/contact/app/${appId}/username-password`, {
      ignoreInterceptor401,
    })
  }

  async getLinkCredentialsData(
    appId: string,
    ignoreInterceptor401?: boolean
  ): Promise<AxiosResponse<TLinkCredentialsData>> {
    return await this.client.get(`/contact/app/${appId}/unlock-link`, { ignoreInterceptor401 })
  }

  async getLTPData(
    appId: string,
    pageSetId: string,
    ignoreInterceptor401?: boolean
  ): Promise<AxiosResponse<TLTPData>> {
    return await this.client.get(`/contact/app/${appId}/ltp-data`, {
      params: {
        page_set_id: pageSetId,
      },
      ignoreInterceptor401,
    })
  }

  async confirmEmailCampaign(
    pageSetId: string,
    emailCampaign: string | undefined | null,
    ltpEmailCampaign: string | undefined | null,
    ignoreInterceptor401?: boolean
  ): Promise<AxiosResponse<void>> {
    const payload: Record<string, string> = {
      page_set_id: pageSetId,
    }
    if (emailCampaign) {
      payload.email_campaign = emailCampaign
    }
    if (ltpEmailCampaign) {
      payload.ltp_email_campaign = ltpEmailCampaign
    }
    return await this.client.post(`/contact/email-campaign-confirm`, payload, {
      ignoreInterceptor401,
    })
  }

  // v2 endpoints
  async sendVerificationCode(payload: TSendVerificationCodePayload): Promise<AxiosResponse> {
    return await this.client.post(`contact/send-verification-code`, payload)
  }

  async verifyEmailV2(
    payload: TVerificationV2Payload
  ): Promise<AxiosResponse<TVerificationV2Data>> {
    return await this.client.post(`/contact/verify-email-code-v2`, payload)
  }
  async subscribePayPalPlanV2(
    payload: TSubscribePayPalV2Payload
  ): Promise<AxiosResponse<TSubscriptionPayPalPaymentIntentV2Data>> {
    return await this.client.post(`/contact/subscribe/paypal_v2`, payload)
  }

  async createPayPalSubscription(
    payload: TCreatePayPalSubscriptionPayload
  ): Promise<AxiosResponse<TCreatePayPalSubscriptionResponse>> {
    return await this.client.post(`/contact/paypal/subscription`, payload)
  }

  async subscribeStripeV2(
    payload: TSubscribeStripeV2Payload
  ): Promise<AxiosResponse<TSubscriptionPaymentIntentV2Data>> {
    return await this.client.post(`/contact/subscribe/stripe_v2`, payload)
  }

  // lifetime purchases v1 checkout
  async fastPurchaseContent(
    payload: TFastPurchaseContentPayload
  ): Promise<AxiosResponse<TFastPurchaseContentData>> {
    return await this.client.post(`/contact/purchase-content/fast`, payload)
  }

  async purchaseContent(
    payload: TPurchaseContentPayload
  ): Promise<AxiosResponse<TPurchaseContentData>> {
    return await this.client.post(`/contact/purchase-content`, payload)
  }

  async createPayPalPurchase(
    payload: TCreatePayPalPurchasePayload
  ): Promise<AxiosResponse<TCreatePayPalPurchaseResponse>> {
    return await this.client.post(`/contact/paypal/purchase`, payload)
  }

  async approvePayPalPurchase(
    payload: TApprovePayPalPurchasePayload
  ): Promise<AxiosResponse<TApprovePayPalPurchaseIntentData>> {
    return await this.client.post(`/contact/paypal/purchase-approve`, payload)
  }
  // lifetime purchases v2 checkout
}
export const ApiService = new ApiServiceImpl()

class EventsApiServiceImpl extends BaseApiService {
  async trackEvents(
    events: TAnalyticsEvent[],
    headers?: RawAxiosRequestHeaders
  ): Promise<AxiosResponse> {
    const payload = JSON.stringify({ events })
    return await this.client.post(`/events`, payload, { headers })
  }
}

export const EventsApiService = new EventsApiServiceImpl('eventsService')
